import type {
  SesDnsRecord,
  SesReadiness,
} from "@redotech/admin-sdk/rpc/schema/support/custom-email-sender";

/** Stable messages the helpdesk SES transport logs when it refuses a send. */
export const SES_REFUSAL_LOG_MESSAGES = [
  "Custom email send refused: integration not connected",
  "Custom email send refused: sender domain not verified for SES",
  "Custom email send refused: SES helpdesk configuration set unset",
] as const;

export function dnsRecordLabel(record: SesDnsRecord, domain: string): string {
  if (record.type === "MX") {
    return "MAIL FROM MX";
  }
  if (record.host.includes("._domainkey.")) {
    return "DKIM";
  }
  if (record.host.startsWith("_dmarc.")) {
    return "DMARC";
  }
  if (record.value.startsWith("v=spf1")) {
    return record.host === domain ? "Root SPF" : "MAIL FROM SPF";
  }
  return record.type;
}

function recordInstructions(
  record: SesDnsRecord,
  index: number,
  domain: string,
): string {
  const lines = [
    `   ${String.fromCharCode(97 + index)}) ${dnsRecordLabel(record, domain)} (${record.required ? "required" : "recommended"})`,
    `      Type: ${record.type}`,
    `      Host / name: ${record.hostPrefix}`,
    `        (full name: ${record.host} — use the full name only if your DNS provider does not add your domain automatically)`,
  ];
  if (record.priority !== null) {
    lines.push(
      `      Priority: ${record.priority} (enter this in the separate priority field)`,
    );
  }
  lines.push(`      Value: ${record.value}`, "      TTL: automatic / default");
  return lines.join("\n");
}

/** A numbered, plain-text version of the merchant's steps for a support reply. */
export function buildMerchantInstructions(
  email: string,
  readiness: SesReadiness,
): string {
  const records = readiness.dnsRecords;
  const sections = [
    `To finish setting up ${email} for sending, please add these DNS records for ${readiness.domain} at your DNS provider (for example Cloudflare, GoDaddy, Namecheap, or Route 53):`,
    "",
    "1. Sign in to your DNS provider and open the DNS records for your domain.",
    "",
    "2. Add each record below exactly as written:",
    "",
    ...records.map((record, index) =>
      recordInstructions(record, index, readiness.domain),
    ),
    "",
    "3. Things that commonly go wrong:",
    "   - Many DNS providers add your domain to the end of the host automatically. If yours does, paste only the short host shown above. Otherwise the name ends up doubled, like name.example.com.example.com.",
    '   - On Cloudflare, set each record to "DNS only" (grey cloud), not "Proxied".',
    "   - For the MX record, enter 10 in the separate priority field, not in the value.",
    "   - Don't add a second SPF record at your root domain. A domain can have only one. If you already have one, add include:amazonses.com to it. The SPF record on the MAIL FROM subdomain is a separate record on a different host.",
    "   - DMARC is optional but recommended. If you already have a DMARC record, keep it and skip ours.",
    "",
    "4. Reply to let us know once the records are added. DNS changes usually show up within minutes but can take up to 48 hours. We'll confirm on our side once your domain is verified.",
  ];
  return sections.join("\n");
}
